import { Checkbox } from "@/components/ui/Checkbox";
import {
  ToolSelectionPanel,
  type ToolAnnotation,
  type ToolSelectionChange,
  type ToolSelectionMode,
  type ToolSelectionServer,
  type ToolSelectionTool,
  type ToolSelectionToolRef,
} from "@/components/tool-selection/ToolSelectionPanel";
import { useOrganization } from "@/contexts/Auth";
import { cn } from "@/lib/utils";
import { mcpServerRouteParam } from "@/lib/sources";
import { useToolMetadata } from "@/hooks/useToolMetadata";
import { useRoutes } from "@/routes";
import { ArrowUpRight, Check, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import type { Selector } from "@gram/client/models/components/selector.js";

import {
  selectorsAfterToolBatch,
  selectorsAfterToolToggle,
} from "./toolSelectionTransition";
import type { ActivePanel, AnnotationHint, ResourceType } from "./types";
import {
  ANNOTATION_TO_DISPOSITION,
  DISPOSITION_TO_ANNOTATION,
  isProjectFilteredResourceType,
  isProjectSelectableResourceType,
  isUnrestrictedResourceType,
  unrestrictedResourceLabel,
} from "./types";
import { computePanelState } from "./computePanelState";
import { type ServerGroup, type ServerTool } from "./serverMerge";
import { useOrgMcpServers } from "./useOrgMcpServers";
import { useOrgAssistants } from "./useOrgAssistants";
import { toolMetadataToServerTools } from "./remoteToolMetadata";

/**
 * What an empty server list means. The inventory is withheld until both org
 * reads succeed, so an empty `groups` is only ever "this org has no servers"
 * after a settled, successful read — while pending or failed it says so.
 */
function serverListMessage({
  settled,
  isError,
  hasServers,
}: {
  settled: boolean;
  isError: boolean;
  hasServers: boolean;
}): string {
  if (isError) return "Servers unavailable";
  if (!settled) return "Loading servers…";
  if (!hasServers) return "No servers found";
  return "No matching servers";
}

interface GrantRuleDrawerContentProps {
  /** The resource type determines which resource list to show */
  resourceType: ResourceType;
  /** The scope slug this picker is for (e.g. "mcp:connect") */
  scope?: string;
  /** null = unrestricted; Selector[] = constrained */
  selectors: Selector[] | null;
  onChangeSelectors: (selectors: Selector[] | null) => void;
  /** Selected annotation hints for auto-group matching */
  annotations?: AnnotationHint[];
  onChangeAnnotations?: (annotations: AnnotationHint[]) => void;
  /** Whether this picker is editing an exception rule (hides "All" option, affects descriptions). */
  isDeny?: boolean;
  /** Restrict which scope-level panels are visible (e.g. ["projects"] for exception rules).
   *  When set, auto-switches to the first allowed panel if current panel isn't in the list. */
  allowedPanels?: ActivePanel[];
  /** When editing an exception rule, pass the allow rule's selectors here.
   *  The picker will filter projects/servers/tools to only those covered by the allow. */
  allowSelectors?: Selector[] | null;
}

export function GrantRuleDrawerContent({
  resourceType,
  scope,
  selectors,
  onChangeSelectors,
  annotations,
  onChangeAnnotations,
  isDeny: isDenyProp,
  allowedPanels,
  allowSelectors,
}: GrantRuleDrawerContentProps): JSX.Element {
  const organization = useOrganization();
  const inventory = useOrgMcpServers(resourceType === "mcp");
  const mcpServers = inventory.groups;
  const assistantInventory = useOrgAssistants(
    isProjectFilteredResourceType(resourceType),
  );
  // Project slug per id, so the tool picker can name each remote server's
  // project when fetching its (project-scoped) stored tool metadata.
  const projectSlugById = useMemo(
    () => new Map(organization.projects.map((p) => [p.id, p.slug])),
    [organization.projects],
  );
  // Override for when user clicks a mode but selectors are still empty
  const [panelOverride, setPanelOverride] = useState<ActivePanel | null>(null);
  const [resourceSearch, setResourceSearch] = useState("");

  const resourceListRef = useRef<HTMLDivElement>(null);
  const handleResourceWheel = useCallback((e: React.WheelEvent) => {
    if (resourceListRef.current) {
      resourceListRef.current.scrollTop += e.deltaY;
    }
  }, []);

  const isMcpConnect = scope === "mcp:connect";
  const projectSelectable = isProjectSelectableResourceType(resourceType);
  const projectFiltered = isProjectFilteredResourceType(resourceType);

  const panelState = computePanelState(selectors, resourceType);
  // Use override only when selectors are empty (user just switched mode)
  const activePanel =
    selectors !== null && selectors.length === 0 && panelOverride
      ? panelOverride
      : panelState.activePanel;

  // panelOverride persists until the user explicitly switches panels via
  // switchPanel(). The derivation above already ignores the override when
  // selectors have content, so clearing it eagerly only causes the UI to
  // jump back to "servers" when the user deselects all items.

  // Auto-switch to first allowed panel when current panel isn't permitted.
  // Fires on mount when allowedPanels constrains the picker (e.g. exception rules).
  useEffect(() => {
    if (!allowedPanels || allowedPanels.length === 0) return;
    if (allowedPanels.includes(activePanel)) return;
    switchPanel(allowedPanels[0]!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedPanels?.join(",")]);

  // Derive allowed project/server IDs from the allow rule's selectors.
  // When an exception picker is open, only show resources the allow rule covers.
  const allowFilter = useMemo(() => {
    // null or undefined = no filtering (allow covers everything)
    if (allowSelectors === null || allowSelectors === undefined) return null;
    const projectIds = new Set<string>();
    const serverIds = new Set<string>();
    for (const s of allowSelectors) {
      const named = s.resourceId && s.resourceId !== "*";
      // A selector pairing a project with one server allows that server, not
      // the project: only a project-wide selector covers the whole group.
      if (s.projectId && !named) projectIds.add(s.projectId);
      if (named) {
        if (projectSelectable) projectIds.add(s.resourceId!);
        else serverIds.add(s.resourceId!);
      }
    }
    return {
      projectIds: projectIds.size > 0 ? projectIds : null,
      serverIds: serverIds.size > 0 ? serverIds : null,
    };
  }, [allowSelectors, projectSelectable]);

  const projectList = useMemo(() => {
    const seen = new Set<string>();
    const projects: { id: string; name: string }[] = [];
    // Include projects from org context
    for (const p of organization.projects) {
      if (!seen.has(p.id)) {
        seen.add(p.id);
        projects.push({ id: p.id, name: p.name });
      }
    }
    // For MCP scopes, also include projects discovered via server groups
    // (ensures project list matches what's visible in the server picker)
    for (const group of mcpServers) {
      if (!seen.has(group.projectId)) {
        seen.add(group.projectId);
        projects.push({ id: group.projectId, name: group.projectName });
      }
    }
    // A permission can allow a project *and* a server in another project, so
    // the two lists are a union: a project is covered if the rule names it, or
    // if it holds a server the rule names.
    if (!allowFilter?.projectIds && !allowFilter?.serverIds) return projects;
    const allowedProjectIds = new Set(allowFilter.projectIds ?? []);
    if (allowFilter.serverIds) {
      for (const group of mcpServers) {
        if (group.servers.some((s) => allowFilter.serverIds!.has(s.id))) {
          allowedProjectIds.add(group.projectId);
        }
      }
    }
    return projects.filter((p) => allowedProjectIds.has(p.id));
  }, [organization.projects, mcpServers, allowFilter]);

  const filteredProjectList = useMemo(
    () =>
      resourceSearch
        ? projectList.filter((p) =>
            p.name.toLowerCase().includes(resourceSearch.toLowerCase()),
          )
        : projectList,
    [projectList, resourceSearch],
  );

  // Pre-filter mcpServers by allow scope, then apply search
  const scopedMcpServers = useMemo(() => {
    if (!allowFilter) return mcpServers;
    return mcpServers
      .map((group) => {
        // A whole project the rule names brings all its servers with it.
        if (allowFilter.projectIds?.has(group.projectId)) return group;
        // Otherwise only the servers the rule names itself, which may sit in
        // a project the rule does not name.
        if (allowFilter.serverIds) {
          return {
            ...group,
            servers: group.servers.filter((s) =>
              allowFilter.serverIds!.has(s.id),
            ),
          };
        }
        return allowFilter.projectIds ? { ...group, servers: [] } : group;
      })
      .filter((g) => g.servers.length > 0);
  }, [mcpServers, allowFilter]);

  const filteredMcpServers = useMemo(() => {
    if (!resourceSearch) return scopedMcpServers;
    const q = resourceSearch.toLowerCase();
    return scopedMcpServers
      .map((group) => ({
        ...group,
        servers: group.servers.filter(
          (s) =>
            s.name.toLowerCase().includes(q) ||
            group.projectName.toLowerCase().includes(q),
        ),
      }))
      .filter((g) => g.servers.length > 0);
  }, [scopedMcpServers, resourceSearch]);

  // What the rule already names, pinned above the list: the chosen row is
  // otherwise somewhere down a long scroll, so "except 1 server" never says
  // which one. Search does not filter these.
  // Only project-wide selectors count as a selected project: one that pairs a
  // project with a named server allows that server, and pinning the project
  // would let its toggle drop the server selector.
  const selectedProjectIds = useMemo(
    () =>
      new Set(
        (selectors ?? []).flatMap((s) =>
          s.projectId && s.resourceId === "*" ? [s.projectId] : [],
        ),
      ),
    [selectors],
  );

  const selectedResourceIds = useMemo(
    () =>
      new Set(
        (selectors ?? []).flatMap((s) =>
          s.resourceId && s.resourceId !== "*" && !s.tool ? [s.resourceId] : [],
        ),
      ),
    [selectors],
  );

  const selectedProjectRows = useMemo(
    () => projectList.filter((p) => selectedProjectIds.has(p.id)),
    [projectList, selectedProjectIds],
  );

  const selectedResourceProjects = useMemo(
    () => projectList.filter((p) => selectedResourceIds.has(p.id)),
    [projectList, selectedResourceIds],
  );

  const selectedServerRows = useMemo(() => {
    if (selectedResourceIds.size === 0) return [];
    return mcpServers.flatMap((group) =>
      group.servers
        .filter((s) => selectedResourceIds.has(s.id))
        .map((server) => ({ server, projectName: group.projectName })),
    );
  }, [mcpServers, selectedResourceIds]);

  const filteredAssistantGroups = useMemo(() => {
    const scoped = allowFilter
      ? assistantInventory.groups
          .map((group) => {
            if (allowFilter.projectIds?.has(group.projectId)) return group;
            return {
              ...group,
              assistants: group.assistants.filter(
                (a) => allowFilter.serverIds?.has(a.id) ?? false,
              ),
            };
          })
          .filter((g) => g.assistants.length > 0)
      : assistantInventory.groups;
    if (!resourceSearch) return scoped;
    const q = resourceSearch.toLowerCase();
    return scoped
      .map((group) => ({
        ...group,
        assistants: group.assistants.filter(
          (a) =>
            a.name.toLowerCase().includes(q) ||
            group.projectName.toLowerCase().includes(q),
        ),
      }))
      .filter((g) => g.assistants.length > 0);
  }, [assistantInventory.groups, allowFilter, resourceSearch]);

  const selectedAssistantRows = useMemo(() => {
    if (selectedResourceIds.size === 0) return [];
    return assistantInventory.groups.flatMap((group) =>
      group.assistants
        .filter((a) => selectedResourceIds.has(a.id))
        .map((assistant) => ({ assistant, projectName: group.projectName })),
    );
  }, [assistantInventory.groups, selectedResourceIds]);

  // The "Specific tools" picker shows servers with enumerable deploy-time tools
  // plus remote/tunneled (dynamic-tools) servers. Remote-backed ones resolve
  // their tools from the stored metadata table on expand; tunneled ones stay a
  // non-selectable row. Proxy servers (no tools/list at deploy time) appear in
  // the "Specific servers" picker for server-level grants but must not render a
  // zero-tools row here.
  const toolPanelMcpServers = useMemo(
    () =>
      scopedMcpServers
        .map((g) => ({
          ...g,
          servers: g.servers.filter(
            (s) => s.tools.length > 0 || s.dynamicTools,
          ),
        }))
        .filter((g) => g.servers.length > 0),
    [scopedMcpServers],
  );

  // Fixed-scope permissions have no resource picker — their granularity is
  // baked into the scope definition. The role editor only offers unrestricted
  // grants for these resources.
  if (isUnrestrictedResourceType(resourceType)) {
    return (
      <span className="border-input text-muted-foreground inline-flex h-7 items-center border bg-transparent px-2 py-1 text-xs">
        {unrestrictedResourceLabel(resourceType)}
      </span>
    );
  }

  const resourceKind =
    projectSelectable || projectFiltered ? resourceType : "mcp";

  // For MCP scopes, `id` is `Server.id`, which serverMerge.ts guarantees is
  // the id enforcement checks (toolset id for toolset-backed servers, the
  // mcp_servers id for remote/tunneled) — see the GRANT ID INVARIANT there.
  const toggleResource = (id: string) => {
    if (selectors === null) return;
    const has = selectors.some(
      (s) => s.resourceKind === resourceKind && s.resourceId === id,
    );
    if (has) {
      onChangeSelectors(
        selectors.filter(
          (s) => !(s.resourceKind === resourceKind && s.resourceId === id),
        ),
      );
    } else {
      onChangeSelectors([...selectors, { resourceKind, resourceId: id }]);
    }
  };

  const isResourceSelected = (id: string) =>
    selectors?.some((s) => s.resourceId === id) ?? false;

  // A project is chosen only by a project-wide selector, so the row's checkbox,
  // its pinned entry, and what the toggle removes all read the same thing: a
  // selector naming one server inside the project is that server's, not the
  // project's, and unchecking the project must not take it away.
  const isProjectSelected = (projectId: string) =>
    selectedProjectIds.has(projectId);

  const toggleProject = (projectId: string) => {
    if (selectors === null) return;
    if (isProjectSelected(projectId)) {
      onChangeSelectors(
        selectors.filter(
          (s) => !(s.projectId === projectId && s.resourceId === "*"),
        ),
      );
    } else {
      onChangeSelectors([
        ...selectors,
        {
          resourceKind: projectFiltered ? resourceType : "mcp",
          resourceId: "*",
          projectId,
        },
      ]);
    }
  };

  const switchPanel = (panel: ActivePanel) => {
    setPanelOverride(panel);
    setResourceSearch("");
    if (panel === "all") {
      onChangeSelectors(null);
    } else {
      onChangeSelectors([]);
    }
    if (panel !== "tools") {
      onChangeAnnotations?.([]);
    }
  };

  const isPanelAllowed = (panel: ActivePanel) =>
    !allowedPanels || allowedPanels.includes(panel);

  const renderScopeOptions = () => (
    <div className="border-border divide-border shrink-0 divide-y border">
      {!isDenyProp && isPanelAllowed("all") && (
        <ScopeOption
          label={allScopeLabel(projectSelectable, projectFiltered)}
          description={allScopeDescription(projectSelectable, projectFiltered)}
          selected={activePanel === "all"}
          onClick={() => switchPanel("all")}
        />
      )}
      {(resourceType === "mcp" || projectFiltered) &&
        isPanelAllowed("projects") && (
          <ScopeOption
            label="Specific projects"
            description={
              projectFiltered
                ? "Give access to assistants within specific projects in your org"
                : "Give access to servers within specific projects in your org"
            }
            selected={activePanel === "projects"}
            onClick={() => switchPanel("projects")}
          />
        )}
      {isPanelAllowed("servers") && (
        <ScopeOption
          label={
            projectFiltered
              ? "Specific assistants"
              : projectSelectable
                ? "Specific projects"
                : "Specific servers"
          }
          description={
            projectFiltered
              ? "Give access to specific assistants across your org"
              : projectSelectable
                ? "Give access to specific projects in your org"
                : "Give access to specific servers across your org"
          }
          selected={activePanel === "servers"}
          onClick={() => switchPanel("servers")}
        />
      )}
      {isMcpConnect && isPanelAllowed("tools") && (
        <ScopeOption
          label="Specific tools"
          description="Give fine-grained access to individual tools"
          selected={activePanel === "tools"}
          onClick={() => switchPanel("tools")}
        />
      )}
    </div>
  );

  const resourceList = activePanel === "servers" && !projectFiltered && (
    <>
      <div className="border-border mt-3 flex items-center gap-2 border border-b-0 px-4 py-2.5">
        <input
          type="text"
          placeholder={
            projectSelectable ? "Search projects…" : "Search servers…"
          }
          value={resourceSearch}
          onChange={(e) => setResourceSearch(e.target.value)}
          className="placeholder:text-muted-foreground flex-1 bg-transparent text-sm outline-none"
        />
        {resourceSearch && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => setResourceSearch("")}
            className="text-muted-foreground hover:text-foreground shrink-0"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
      <div
        ref={resourceListRef}
        onWheel={handleResourceWheel}
        className="border-border divide-border min-h-0 flex-1 divide-y overflow-y-auto border"
      >
        {projectSelectable ? (
          <SelectedGroup count={selectedResourceProjects.length}>
            {selectedResourceProjects.map((project) => (
              <ResourceCheckbox
                key={`selected-${project.id}`}
                id={project.id}
                name={project.name}
                checked
                onToggle={toggleResource}
              />
            ))}
          </SelectedGroup>
        ) : (
          <SelectedGroup count={selectedServerRows.length}>
            {selectedServerRows.map(({ server, projectName }) => (
              <ResourceCheckbox
                key={`selected-${server.id}`}
                id={server.id}
                name={
                  <>
                    <span title={server.name}>{server.name}</span>
                    <span
                      title={projectName}
                      className="text-muted-foreground ml-2 text-xs font-normal"
                    >
                      {projectName}
                    </span>
                  </>
                }
                checked
                onToggle={toggleResource}
              />
            ))}
          </SelectedGroup>
        )}
        {projectSelectable ? (
          filteredProjectList.length === 0 ? (
            <div className="text-muted-foreground px-3 py-3 text-sm">
              {projectList.length === 0
                ? "No projects found"
                : "No matching projects"}
            </div>
          ) : (
            filteredProjectList.map((resource) => (
              <ResourceCheckbox
                key={resource.id}
                id={resource.id}
                name={resource.name}
                checked={isResourceSelected(resource.id)}
                onToggle={toggleResource}
              />
            ))
          )
        ) : filteredMcpServers.length === 0 ? (
          <div className="text-muted-foreground px-3 py-3 text-sm">
            {serverListMessage({
              settled: inventory.settled,
              isError: inventory.isError,
              // The organization's own inventory, not the allow-scoped view:
              // an exception that filters every server out does not mean the
              // organization has none.
              hasServers: mcpServers.length > 0,
            })}
          </div>
        ) : (
          // Grouped under a project heading rather than prefixing every row
          // with the same project name: the servers are what is being chosen.
          filteredMcpServers.map((group) => (
            <div key={group.projectId}>
              <div className="bg-muted/40 text-muted-foreground text-eyebrow border-border border-b px-4 py-1.5">
                {group.projectName}
              </div>
              {group.servers.map((server) => (
                <ResourceCheckbox
                  key={server.id}
                  id={server.id}
                  name={server.name}
                  qualifier={group.projectName}
                  checked={isResourceSelected(server.id)}
                  onToggle={toggleResource}
                />
              ))}
            </div>
          ))
        )}
      </div>
    </>
  );

  const assistantPickerList = activePanel === "servers" && projectFiltered && (
    <>
      <div className="border-border mt-3 flex items-center gap-2 border border-b-0 px-4 py-2.5">
        <input
          type="text"
          placeholder="Search assistants…"
          value={resourceSearch}
          onChange={(e) => setResourceSearch(e.target.value)}
          className="placeholder:text-muted-foreground flex-1 bg-transparent text-sm outline-none"
        />
        {resourceSearch && (
          <button
            type="button"
            aria-label="Clear assistant search"
            onClick={() => setResourceSearch("")}
            className="text-muted-foreground hover:text-foreground shrink-0"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
      <div
        ref={resourceListRef}
        onWheel={handleResourceWheel}
        className="border-border divide-border min-h-0 flex-1 divide-y overflow-y-auto border"
      >
        <SelectedGroup count={selectedAssistantRows.length}>
          {selectedAssistantRows.map(({ assistant, projectName }) => (
            <ResourceCheckbox
              key={`selected-${assistant.id}`}
              id={assistant.id}
              name={
                <>
                  <span title={assistant.name}>{assistant.name}</span>
                  <span
                    title={projectName}
                    className="text-muted-foreground ml-2 text-xs font-normal"
                  >
                    {projectName}
                  </span>
                </>
              }
              checked
              onToggle={toggleResource}
            />
          ))}
        </SelectedGroup>
        {filteredAssistantGroups.length === 0 ? (
          <div className="text-muted-foreground px-3 py-3 text-sm">
            {assistantInventory.isError
              ? "Assistants unavailable"
              : !assistantInventory.settled
                ? "Loading assistants…"
                : assistantInventory.groups.length === 0
                  ? "No assistants found"
                  : "No matching assistants"}
          </div>
        ) : (
          filteredAssistantGroups.map((group) => (
            <div key={group.projectId}>
              <div className="bg-muted/40 text-muted-foreground text-eyebrow border-border border-b px-4 py-1.5">
                {group.projectName}
              </div>
              {group.assistants.map((assistant) => (
                <ResourceCheckbox
                  key={assistant.id}
                  id={assistant.id}
                  name={assistant.name}
                  qualifier={group.projectName}
                  checked={isResourceSelected(assistant.id)}
                  onToggle={toggleResource}
                />
              ))}
            </div>
          ))
        )}
      </div>
    </>
  );

  const projectPickerList = activePanel === "projects" && (
    <>
      <div className="border-border mt-3 flex items-center gap-2 border border-b-0 px-4 py-2.5">
        <input
          type="text"
          placeholder="Search projects…"
          value={resourceSearch}
          onChange={(e) => setResourceSearch(e.target.value)}
          className="placeholder:text-muted-foreground flex-1 bg-transparent text-sm outline-none"
        />
        {resourceSearch && (
          <button
            type="button"
            aria-label="Clear project search"
            onClick={() => setResourceSearch("")}
            className="text-muted-foreground hover:text-foreground shrink-0"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
      <div
        ref={resourceListRef}
        onWheel={handleResourceWheel}
        className="border-border divide-border min-h-0 flex-1 divide-y overflow-y-auto border"
      >
        <SelectedGroup count={selectedProjectRows.length}>
          {selectedProjectRows.map((project) => (
            <ResourceCheckbox
              key={`selected-${project.id}`}
              id={project.id}
              name={project.name}
              checked
              onToggle={toggleProject}
            />
          ))}
        </SelectedGroup>
        {filteredProjectList.length === 0 ? (
          <div className="text-muted-foreground px-3 py-3 text-sm">
            {projectList.length === 0
              ? "No projects found"
              : "No matching projects"}
          </div>
        ) : (
          filteredProjectList.map((project) => (
            <ResourceCheckbox
              key={project.id}
              id={project.id}
              name={project.name}
              checked={isProjectSelected(project.id)}
              onToggle={toggleProject}
            />
          ))
        )}
      </div>
    </>
  );

  const customTabs = (toolScrollClass?: string) => (
    <RoleToolSelectionPanel
      mcpServers={toolPanelMcpServers}
      projectSlugById={projectSlugById}
      selectors={selectors ?? []}
      annotations={annotations}
      onChangeAnnotations={onChangeAnnotations}
      isDeny={!!isDenyProp}
      onChangeSelectors={(sels) => onChangeSelectors(sels)}
      onToggleTool={(serverId, toolName) => {
        const sels = selectors ?? [];
        const exists = sels.some(
          (s) => s.resourceId === serverId && s.tool === toolName,
        );
        if (exists) {
          onChangeSelectors(
            sels.filter(
              (s) => !(s.resourceId === serverId && s.tool === toolName),
            ),
          );
        } else {
          onChangeSelectors([
            ...sels,
            {
              resourceKind: "mcp",
              resourceId: serverId,
              tool: toolName,
            },
          ]);
        }
      }}
      onBatchToggleTools={(serverId, toolNames, select) => {
        const sels = selectors ?? [];
        if (select) {
          const existing = new Set(
            sels
              .filter((s) => s.resourceId === serverId && s.tool)
              .map((s) => s.tool!),
          );
          const toAdd = toolNames
            .filter((name) => !existing.has(name))
            .map((name) => ({
              resourceKind: "mcp" as const,
              resourceId: serverId,
              tool: name,
            }));
          onChangeSelectors([...sels, ...toAdd]);
        } else {
          const toolSet = new Set(toolNames);
          onChangeSelectors(
            sels.filter(
              (s) =>
                !(s.resourceId === serverId && s.tool && toolSet.has(s.tool)),
            ),
          );
        }
      }}
      className={cn("min-h-[200px]", toolScrollClass)}
    />
  );

  return (
    <div className="flex flex-1 flex-col px-1.5 pb-1.5">
      {renderScopeOptions()}
      {isMcpConnect && !isDenyProp && (
        <p className="text-muted-foreground mt-2 text-xs">
          Plugins shared with this role receive the servers chosen here. Read
          and write access also let members connect, but never add servers to
          plugins.
        </p>
      )}
      {/* The server inventory is withheld unless both org listings succeeded,
          so say why the lists are empty rather than implying the org has no
          servers. */}
      {assistantInventory.isError && (
        <div
          role="alert"
          className="border-border text-muted-foreground mt-3 flex items-center justify-between gap-2 border px-3 py-2 text-sm"
        >
          <span>Could not load this organization&rsquo;s assistants.</span>
          <button
            type="button"
            onClick={assistantInventory.refetch}
            className="text-foreground underline decoration-dotted underline-offset-4 hover:decoration-solid"
          >
            Retry
          </button>
        </div>
      )}
      {inventory.isError && (
        <div
          role="alert"
          className="border-border text-muted-foreground mt-3 flex items-center justify-between gap-2 border px-3 py-2 text-sm"
        >
          <span>Could not load this organization&rsquo;s MCP servers.</span>
          <button
            type="button"
            onClick={inventory.refetch}
            className="text-foreground underline decoration-dotted underline-offset-4 hover:decoration-solid"
          >
            Retry
          </button>
        </div>
      )}
      {/* The area below the options is a fixed height whichever option is
          chosen. Without it, picking "Specific servers" grew the dialog by
          the height of the list and moved the options out from under the
          pointer. */}
      <div className="mt-3 flex h-[320px] max-h-full min-h-0 flex-col overflow-y-auto">
        {activePanel === "all" ? (
          <div className="border-border text-muted-foreground flex flex-1 items-center justify-center border px-6 text-center text-sm">
            {isDenyProp
              ? "This exception covers everything the allow rule permits."
              : projectFiltered
                ? "This role reaches every assistant in every project."
                : projectSelectable
                  ? "This role reaches every project in the organization."
                  : "This role reaches every server in every project."}
          </div>
        ) : (
          <>
            {resourceList}
            {assistantPickerList}
            {projectPickerList}
            {activePanel === "tools" && (
              <div className="flex min-h-0 flex-1 flex-col">{customTabs()}</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

interface RemoteToolsState {
  tools: ServerTool[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * Fetches stored remote-MCP tool metadata for one server and reports it
 * upward. Mounted per remote-backed server so the picker issues at most one
 * metadata request per expanded server; `enabled` gates the fetch to rows the
 * admin actually opens.
 */
function RemoteToolMetadataLoader({
  serverId,
  projectSlug,
  enabled,
  onState,
}: {
  serverId: string;
  projectSlug: string | undefined;
  enabled: boolean;
  onState: (serverId: string, state: RemoteToolsState) => void;
}): null {
  const { metadataByTool, isLoading, isError, refetch } = useToolMetadata(
    serverId,
    { enabled, projectSlug },
  );
  const refetchRef = useRef(refetch);
  useEffect(() => {
    refetchRef.current = refetch;
  });
  const stableRefetch = useCallback(() => refetchRef.current(), []);
  const tools = useMemo(
    () => toolMetadataToServerTools(serverId, Object.values(metadataByTool)),
    [serverId, metadataByTool],
  );
  useEffect(() => {
    if (!enabled) return;
    onState(serverId, { tools, isLoading, isError, refetch: stableRefetch });
  }, [enabled, serverId, tools, isLoading, isError, stableRefetch, onState]);
  return null;
}

function serverToolAnnotations(tool: ServerTool): ToolAnnotation[] {
  const annotations: ToolAnnotation[] = [];
  const hints = tool.annotations;
  if (hints?.readOnlyHint) annotations.push("read_only");
  if (hints?.destructiveHint) annotations.push("destructive");
  if (hints?.idempotentHint) annotations.push("idempotent");
  if (hints?.openWorldHint) annotations.push("open_world");
  return annotations;
}

function toSelectionTool(tool: ServerTool): ToolSelectionTool {
  return { name: tool.name, annotations: serverToolAnnotations(tool) };
}

function remoteServerStatus(
  state: RemoteToolsState | undefined,
): ToolSelectionServer["status"] {
  if (!state || state.isLoading) return "loading";
  if (state.isError) return "error";
  return "ready";
}

/**
 * Link to the MCP server's Inspect tab, correctly scoped to the server's
 * project. Uses useRoutes with the project slug override so the link works
 * when rendered from org-level pages (where the ambient projectSlug is
 * undefined).
 */
function InspectTabLink({
  serverId,
  serverSlug,
  projectSlug,
}: {
  serverId: string;
  serverSlug: string;
  projectSlug?: string;
}) {
  const routes = useRoutes({ projectSlug });
  return (
    <Link
      to={routes.mcp.details.inspect.href(
        mcpServerRouteParam({ id: serverId, slug: serverSlug }),
      )}
      className="text-primary inline-flex items-center gap-1 hover:underline"
    >
      Connect it on the Inspect tab
      <ArrowUpRight className="h-3 w-3" />
    </Link>
  );
}

/**
 * Dashboard adapter around the shared ToolSelectionPanel: converts server
 * groups and selectors into the panel's normalized types, lazily loads remote
 * tool metadata, and replays panel toggles as the legacy selector/annotation
 * updates so emitted grants are unchanged.
 */
function RoleToolSelectionPanel({
  mcpServers,
  projectSlugById,
  selectors,
  onToggleTool,
  onBatchToggleTools,
  annotations,
  onChangeAnnotations,
  onChangeSelectors,
  isDeny,
  className,
}: {
  mcpServers: ServerGroup[];
  projectSlugById: Map<string, string>;
  selectors: Selector[];
  onToggleTool: (serverId: string, toolName: string) => void;
  onBatchToggleTools?: (
    serverId: string,
    toolNames: string[],
    select: boolean,
  ) => void;
  annotations?: AnnotationHint[];
  onChangeAnnotations?: (annotations: AnnotationHint[]) => void;
  onChangeSelectors?: (selectors: Selector[]) => void;
  isDeny?: boolean;
  className?: string;
}): JSX.Element {
  const allServers = useMemo(
    () =>
      mcpServers
        .flatMap((g) =>
          g.servers.map((s) => ({
            ...s,
            projectName: g.projectName,
            projectSlug: projectSlugById.get(g.projectId),
          })),
        )
        .sort((a, b) =>
          `${a.projectName}/${a.name}`.localeCompare(
            `${b.projectName}/${b.name}`,
          ),
        ),
    [mcpServers, projectSlugById],
  );

  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(
    new Set(),
  );
  const [remoteTools, setRemoteTools] = useState<
    Record<string, RemoteToolsState>
  >({});

  const handleExpandedServersChange = useCallback((serverIds: string[]) => {
    setExpandedIds(new Set(serverIds));
  }, []);

  const handleRemoteToolsState = useCallback(
    (serverId: string, state: RemoteToolsState) => {
      setRemoteTools((prev) => ({ ...prev, [serverId]: state }));
    },
    [],
  );

  const panelServers = useMemo(
    (): ToolSelectionServer[] =>
      allServers.map((server) => {
        const namePrefix = `${server.projectName.toLowerCase()}/`;
        const isRemoteBacked = server.dynamicTools && server.remoteBacked;
        const isTunneled = server.dynamicTools && !server.remoteBacked;
        if (isTunneled) {
          return {
            id: server.id,
            name: server.name,
            namePrefix,
            tools: [],
            status: "unavailable",
            unavailableLabel: "dynamic tools",
            unavailableTooltip:
              "Tools are dynamically resolved for this server and cannot be individually permissioned.",
          };
        }
        if (isRemoteBacked) {
          const state = remoteTools[server.id];
          const status = remoteServerStatus(state);
          return {
            id: server.id,
            name: server.name,
            namePrefix,
            tools:
              status === "ready" && state
                ? state.tools.map(toSelectionTool)
                : [],
            status,
            collapsedLabel: "Expand to load tools",
            emptyLabel: "Not synced",
            emptyContent: (
              <div className="text-muted-foreground space-y-1 px-8 py-3 text-sm">
                <p>This server&apos;s tools haven&apos;t been synced yet.</p>
                <p>
                  <InspectTabLink
                    serverId={server.id}
                    serverSlug={server.slug}
                    projectSlug={server.projectSlug}
                  />{" "}
                  to permission its tools individually.
                </p>
              </div>
            ),
            onRetry: state?.refetch,
          };
        }
        return {
          id: server.id,
          name: server.name,
          namePrefix,
          tools: server.tools.map(toSelectionTool),
          status: "ready",
        };
      }),
    [allServers, remoteTools],
  );

  // Annotation counts stay pinned to deploy-time tools: lazily loaded remote
  // metadata must not change the chip counts when a row is expanded.
  const toolCountByAnnotation = useMemo(() => {
    const counts = new Map<ToolAnnotation, number>();
    for (const server of allServers) {
      for (const tool of server.tools) {
        for (const annotation of serverToolAnnotations(tool)) {
          counts.set(annotation, (counts.get(annotation) ?? 0) + 1);
        }
      }
    }
    return counts;
  }, [allServers]);

  const selectedAnnotations = useMemo(
    () => (annotations ?? []).map((hint) => ANNOTATION_TO_DISPOSITION[hint]),
    [annotations],
  );

  const selectedTools = useMemo(
    () =>
      selectors.flatMap((s): ToolSelectionToolRef[] =>
        s.tool ? [{ serverId: s.resourceId, toolName: s.tool }] : [],
      ),
    [selectors],
  );

  const hasManualTools = selectors.some(
    (s) => s.tool && s.resourceId && s.resourceId !== "*",
  );
  const mode: ToolSelectionMode = hasManualTools ? "tools" : "annotations";

  const handleSelectionChange = (change: ToolSelectionChange) => {
    switch (change.kind) {
      case "annotation-toggle": {
        if (!onChangeAnnotations || !onChangeSelectors) return;
        const hints = change.annotations.map(
          (a) => DISPOSITION_TO_ANNOTATION[a]!,
        );
        onChangeAnnotations(hints);
        onChangeSelectors(
          hints.map((hint) => ({
            resourceKind: "mcp" as const,
            resourceId: "*",
            disposition: ANNOTATION_TO_DISPOSITION[hint],
          })),
        );
        return;
      }
      case "tool-toggle": {
        if ((annotations ?? []).length > 0) {
          // Entering manual mode replaces the authoritative selectors
          // atomically: keeping the category-wide disposition selectors
          // alongside the first tool pick would silently widen the
          // persisted grant past the panel's exclusive-mode promise.
          onChangeAnnotations?.([]);
          onChangeSelectors?.(
            selectorsAfterToolToggle(
              selectors,
              change.serverId,
              change.toolName,
            ),
          );
          return;
        }
        onToggleTool(change.serverId, change.toolName);
        return;
      }
      case "tool-batch": {
        if ((annotations ?? []).length > 0) {
          onChangeAnnotations?.([]);
          onChangeSelectors?.(
            selectorsAfterToolBatch(
              selectors,
              change.serverId,
              change.toolNames,
              change.selected,
            ),
          );
          return;
        }
        onBatchToggleTools?.(
          change.serverId,
          change.toolNames,
          change.selected,
        );
        return;
      }
    }
  };

  const remoteBackedServers = allServers.filter(
    (s) => s.dynamicTools && s.remoteBacked,
  );

  return (
    <>
      {remoteBackedServers.map((server) => (
        <RemoteToolMetadataLoader
          key={server.id}
          serverId={server.id}
          projectSlug={server.projectSlug}
          enabled={expandedIds.has(server.id)}
          onState={handleRemoteToolsState}
        />
      ))}
      <ToolSelectionPanel
        servers={panelServers}
        mode={mode}
        selectedAnnotations={selectedAnnotations}
        selectedTools={selectedTools}
        onSelectionChange={handleSelectionChange}
        annotationSelectionSupported={!!onChangeAnnotations}
        annotationsDescription="Tools can be annotated with labels that provide more context about the properties of the tool, such as if it's a destructive operation. OpenAPI sources are tagged automatically based on HTTP method. You can edit annotations on the MCP tools tab."
        toolsDescription={
          isDeny
            ? "Select specific tools to exclude. Expand a server to choose which tools this role should not access."
            : "Select specific tools to allow. Expand a server to choose which tools this role can access."
        }
        toolCountByAnnotation={toolCountByAnnotation}
        onExpandedServersChange={handleExpandedServersChange}
        className={className}
      />
    </>
  );
}

/** What the rule already names, held at the top of a picker it could scroll out of. */
function SelectedGroup({
  count,
  children,
}: {
  count: number;
  children: React.ReactNode;
}): JSX.Element | null {
  if (count === 0) return null;
  return (
    <div>
      <div className="bg-muted/40 text-muted-foreground text-eyebrow border-border border-b px-4 py-1.5">
        Selected ({count})
      </div>
      {children}
    </div>
  );
}

function ResourceCheckbox({
  id,
  name,
  qualifier,
  checked,
  onToggle,
  compact,
}: {
  id: string;
  name: React.ReactNode;
  /** Read out after the name, for lists whose grouping is only a heading. */
  qualifier?: string;
  checked: boolean;
  onToggle: (id: string) => void;
  compact?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={() => onToggle(id)}
      className={cn(
        "hover:bg-accent flex w-full cursor-pointer items-center gap-3 px-4",
        compact ? "h-9 text-sm" : "py-1.5 text-sm",
        checked && "font-medium",
      )}
    >
      <Checkbox
        checked={checked}
        className="focus-visible:border-input pointer-events-none focus-visible:ring-0"
        tabIndex={-1}
      />
      <span className="truncate">{name}</span>
      {qualifier && <span className="sr-only">in {qualifier}</span>}
    </button>
  );
}

function ScopeOption({
  label,
  description,
  selected,
  onClick,
}: {
  label: string;
  description?: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "hover:bg-accent flex w-full cursor-pointer items-start gap-3 px-4 py-3 text-left text-sm transition-colors",
        selected && "bg-muted/40 font-medium",
      )}
    >
      <span className="mt-0.5 flex w-4 shrink-0 items-center justify-center">
        {selected && <Check className="h-3.5 w-3.5" />}
      </span>
      <span className="flex flex-col items-start">
        <span>{label}</span>
        {description && (
          <span className="text-muted-foreground text-xs font-normal">
            {description}
          </span>
        )}
      </span>
    </button>
  );
}

function allScopeLabel(projectSelectable: boolean, projectFiltered: boolean) {
  if (projectFiltered) return "All assistants";
  return projectSelectable ? "All projects" : "All servers";
}

function allScopeDescription(
  projectSelectable: boolean,
  projectFiltered: boolean,
) {
  if (projectFiltered) return "Give access to every assistant in your org";
  return projectSelectable
    ? "Give access to every project in your org"
    : "Give access to all servers in every project in your org";
}
